import { useCallback, useEffect, useState } from 'react'
import IssuesService from '../../../services/api/IssuesService'

const INITIAL_PAGY = { page: 1, pages: 0, count: 0, items: 0 }
const noop = () => null

const useTriggeredAlerts = (collectId, {
  page, items, search, issueStatus, issueContent, sort, direction, draftId, columns, variables,
  startDate, endDate, enabled = true
}) => {
  const [issues, setIssues] = useState([])
  const [pagy, setPagy] = useState(INITIAL_PAGY)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [refetchToken, setRefetchToken] = useState(0)

  const refetch = useCallback(() => setRefetchToken(token => token + 1), [])
  // A surgical merge avoids the flicker of a full refetch.
  // Merge, not replace: a status or attribution PATCH omits custom-column values that never change.
  const mergeIssue = useCallback(updated => {
    if (!updated?.id)
      return
    setIssues(previous => previous.map(issue => {
      if (issue.id !== updated.id)
        return issue
      return { ...issue, ...updated }
    }))
  }, [])
  const columnsKey = columns ? columns.join(',') : ''
  const variablesKey = variables ? variables.join(',') : ''

  useEffect(() => {
    if (!enabled)
      return noop
    let cancelled = false
    setLoading(true)
    setError(null)

    const requestParams = {
      page,
      items,
      search,
      issueStatus,
      issueContent,
      sort,
      direction,
      draftId,
      columns,
      variables,
      startDate,
      endDate
    }
    IssuesService.getIssues(collectId, requestParams)
      .then(response => {
        if (cancelled)
          return
        setIssues(response.issues || [])
        setPagy(response.pagy || INITIAL_PAGY)
      })
      .catch(err => {
        if (cancelled)
          return
        setError(err)
        setIssues([])
        setPagy(INITIAL_PAGY)
      })
      .finally(() => {
        if (cancelled)
          return
        setLoading(false)
        setHasLoaded(true)
      })

    return () => {
      cancelled = true
    }
    // Stable string keys avoid re-firing on `columns`/`variables` array reference churn.
  }, [
    collectId,
    page,
    items,
    search,
    issueStatus,
    issueContent,
    sort,
    direction,
    draftId,
    columnsKey,
    variablesKey,
    startDate,
    endDate,
    enabled,
    refetchToken
  ])

  return { issues, pagy, loading, hasLoaded, error, refetch, mergeIssue }
}

export default useTriggeredAlerts
